import { getIndustryConfig } from '../data/industryConfig';
import type { BusinessSettings, ProposalSettings } from '../types';
import type { Idioma } from '../i18n/textos';

/** Stock cover art for the three built-in themes. Shared so the sidebar's crop
 *  preview shows the same image the proposal will render. */
export const COVER_THEME_IMAGES: Record<string, string> = {
    Modern: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80',
    Natural: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&q=80',
    Elegant: 'https://images.unsplash.com/photo-1600607686527-6fb886090705?auto=format&fit=crop&q=80',
};

/**
 * La foto de portada que la propuesta va a pintar de verdad. MANDA EL TEMA.
 *
 * Antes mandaba `customUrl`: `customUrl || COVER_THEME_IMAGES[theme]`. Con eso,
 * en cuanto alguien subía una foto una vez, los botones Moderno / Natural /
 * Elegante dejaban de servir PARA SIEMPRE — se marcaban como elegidos, la
 * propuesta guardaba el tema nuevo, y la portada seguía enseñando la misma foto
 * subida. No había forma de volver atrás desde la interfaz, ni pista de por
 * qué: el clic hacía algo, solo que nada visible.
 *
 * Ahora un tema con foto propia gana, y `Custom` —que no está en el mapa— cae a
 * la foto subida. Las propuestas viejas que quedaron con la URL colgando y un
 * tema de catálogo encima pasan a enseñar el tema, que es justo lo que su dueño
 * pidió al hacer clic.
 *
 * La URL NO se borra al elegir un tema: sigue guardada para que volver a tu
 * foto sea un clic y no otra subida.
 */
export function resolveCoverImageUrl(theme?: string, customUrl?: string): string {
    const delTema = COVER_THEME_IMAGES[theme ?? ''];
    if (delTema) return delTema;
    return customUrl || COVER_THEME_IMAGES.Modern;
}

export interface ProposalDefaultsInput {
    title: string;
    introMessage: string;
    /** Workspace settings — supplies default terms, company-profile text, and upsells.
     *  Optional so callers without settings still get a valid (if leaner) config. */
    settings?: BusinessSettings | null;
    /** El idioma de la app en el momento de crearla. Es el que verá el cliente:
     *  ver `ProposalSettings.idioma`. Se pide aquí —y no se adivina dentro— para
     *  que esto siga siendo una función pura, comprobable sin montar React. */
    idioma?: Idioma;
}

/**
 * P-6: the ONE canonical default ProposalSettings factory.
 *
 * Previously three call sites seeded proposalSettings independently and diverged:
 * EstimatesContext.addEstimate (rich), useEstimateWorkflow.createAndOpenEstimate
 * (thin), and ProposalView's lazy backfill (thin). The thin seeds omitted
 * customTerms / fontFamily / showGallery / companyProfileText / upsells, which
 * produced the "toggle is ON but nothing renders" class of bug. All three now call
 * this so a proposal looks the same however the estimate was created.
 */
export function makeDefaultProposalSettings({ title, introMessage, settings, idioma = 'es' }: ProposalDefaultsInput): ProposalSettings {
    const industry = settings?.profile?.industryType ? getIndustryConfig(settings.profile.industryType) : null;
    return {
        // La propuesta se ENTREGA en inglés siempre (ver `idiomaEntregaDe`).
        // Se sella igualmente para dejar constancia de cómo salió: quien lea
        // esta fila dentro de un año no tiene por qué saber cuál era la regla.
        idioma: 'en',
        // El CONTENIDO, en cambio, nace en el idioma en el que se está
        // trabajando, y eso hay que decirlo con la verdad: si se sellara 'en'
        // sin más, una propuesta escrita en español se daría por buena y se
        // mandaría en español bajo una etiqueta que dice inglés. Diciendo la
        // verdad, la pestaña Enviar pide traducirla, que es lo que la deja
        // realmente en inglés.
        idiomaOriginal: idioma,
        title,
        introMessage,
        coverImageTheme: 'Modern',
        showLineItemPricing: true,
        showTotals: true,
        customTerms: settings?.defaultTerms ?? '',
        fontFamily: 'Sans',
        animationStyle: 'Fade',
        showGallery: false,
        showCompanyProfile: false,
        galleryImages: [],
        companyProfileText: settings?.profile?.businessDescription || industry?.defaultDescription || '',
        upsells: industry?.defaultUpsells ?? [],
    };
}
